import React from 'react'
import { Modal, StyleSheet, TouchableOpacity, View } from 'react-native'
import { Text } from '../atoms'
import { useTheme } from '@react-navigation/native'
import { useLanguage } from '@context'
import { Theme } from '@theme'

type ExtraAction = { label: string; onPress: () => void }

type DeleteModalProps = {
  isVisible: boolean
  setIsVisible: (isVisible: boolean) => void
  title: string
  onDelete: () => void
  description?: string
  deleteLabel?: string
  extraActions?: ExtraAction[]
}

export const DeleteModal = ({
  isVisible,
  setIsVisible,
  title,
  onDelete,
  description,
  deleteLabel,
  extraActions,
}: DeleteModalProps) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyle(theme)

  const close = () => setIsVisible(false)

  return (
    <Modal visible={isVisible} transparent animationType="fade" onRequestClose={close}>
      <View style={styles.overlay}>
        <View style={styles.card}>
          <Text type="subtitleBold">{t('common.confirmDelete')}</Text>
          <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.description}>
            {description ?? t('deleteModal.confirmMessage', { title })}
          </Text>

          <View style={styles.actions}>
            <TouchableOpacity style={styles.cancelBtn} onPress={close}>
              <Text type="body">{t('deleteModal.cancel')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.actionBtn, { backgroundColor: theme.colors.error }]}
              onPress={() => { onDelete(); close() }}
            >
              <Text type="bodyBold" color={theme.colors.white}>
                {deleteLabel ?? t('deleteModal.delete')}
              </Text>
            </TouchableOpacity>

            {extraActions?.map((action, i) => (
              <TouchableOpacity
                key={i}
                style={[styles.actionBtn, { backgroundColor: theme.colors.error }]}
                onPress={() => { action.onPress(); close() }}
              >
                <Text type="bodyBold" color={theme.colors.white}>{action.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>
    </Modal>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.45)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 24,
    },
    card: {
      width: '100%',
      maxWidth: 400,
      backgroundColor: theme.colors.white,
      borderRadius: 16,
      padding: 24,
    },
    description: {
      marginTop: 8,
    },
    actions: {
      marginTop: 20,
      gap: 10,
    },
    cancelBtn: {
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderRadius: 10,
      alignItems: 'center',
      borderWidth: 1,
      borderColor: theme.colors.lightGrey,
    },
    actionBtn: {
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderRadius: 10,
      alignItems: 'center',
    },
  })
